import api from '../services/api';

// Web Push subscription management.
//
// The permission prompt must be triggered by a user gesture, so nothing here
// runs on load — it is all driven from the toggle in the navbar menu.

// VAPID keys travel as base64url; PushManager wants a Uint8Array.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// iOS delivers Web Push only to a PWA installed on the Home Screen — never to a
// Safari tab, on any version. Detecting this is the difference between telling
// someone to install the app and letting them tap a button that cannot work.
export function isIosSafariTab() {
  const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  return isIos && !standalone;
}

export function isIosDevice() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export function getPermission() {
  return isPushSupported() ? Notification.permission : 'unsupported';
}

export async function getExistingSubscription() {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export async function fetchConfig() {
  const { data } = await api.get('/push/config');
  return data; // { enabled, publicKey }
}

// `preloadedConfig` is the config the caller already fetched on mount.
//
// The ordering below is load-bearing. Safari only honours
// Notification.requestPermission() while the user-gesture context from the tap is
// still alive, and ANY intervening await that yields to the network destroys it.
// The request is then denied without ever showing a prompt — and on iOS that
// denial is permanent until the user resets it by hand. So permission is asked
// for first, before the config fetch, and the caller passes in a config it
// already has so there is nothing to await at all.
export async function subscribe(preloadedConfig) {
  if (!isPushSupported()) throw new Error('Push is not supported in this browser.');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    // 'denied' is sticky — the browser will not ask again. The recovery steps
    // differ per platform, so the UI renders them; this just signals which case.
    const err = new Error(
      permission === 'denied'
        ? 'BLOCKED'
        : 'Notification permission was dismissed. Tap the toggle again to retry.'
    );
    err.blocked = permission === 'denied';
    throw err;
  }

  const config = preloadedConfig || (await fetchConfig());
  if (!config.enabled || !config.publicKey) {
    throw new Error('Push notifications are not configured on the server.');
  }

  const registration = await navigator.serviceWorker.ready;

  // Reuse an existing subscription rather than creating a second one; the browser
  // errors if you subscribe twice with a different key.
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      // Required by Chrome: every push must result in a visible notification.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.publicKey),
    });
  }

  const json = subscription.toJSON();
  await api.post('/push/subscribe', {
    endpoint: subscription.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
  });

  return subscription;
}

export async function unsubscribe() {
  const subscription = await getExistingSubscription();
  if (!subscription) return;

  // Tell the server first: if the browser-side unsubscribe succeeded but the
  // server row survived, we would keep sending to a dead endpoint.
  await api.post('/push/unsubscribe', { endpoint: subscription.endpoint, p256dh: '', auth: '' });
  await subscription.unsubscribe();
}

export async function sendTest() {
  const { data } = await api.post('/push/test');
  return data;
}
